import * as THREE from "three";

/**
 * Shared "interior finish" pieces used by the office-style procedural rooms so
 * they read as finished, lit spaces (ceiling + recessed lights, skirting, a
 * daylight window) instead of open-topped boxes. Pure geometry + a couple of
 * cheap point lights — no external assets.
 */

/** A flat ceiling slab. Because it caps the room (the Scene's sun no longer pours
 *  in from the open top), pair it with <CeilingDownlight/>s to keep the interior
 *  lit. */
export function Ceiling({
  y = 4.15,
  w = 12,
  d = 8,
  z = 0.2,
  color = "#eef1f4",
}: {
  y?: number;
  w?: number;
  d?: number;
  z?: number;
  color?: string;
}) {
  return (
    <group>
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, y, z]} receiveShadow>
        <planeGeometry args={[w, d]} />
        <meshStandardMaterial color={color} roughness={1} side={THREE.DoubleSide} />
      </mesh>
      {/* shallow perimeter cove so the ceiling/wall junction isn't a hard seam */}
      <mesh position={[0, y - 0.06, z]}>
        <boxGeometry args={[w, 0.12, d]} />
        <meshStandardMaterial color={color} roughness={1} />
      </mesh>
    </group>
  );
}

/** A recessed downlight: a bright emissive disc in the ceiling + a soft real
 *  point light below it. Keep `intensity` modest and rely on several of them. */
export function CeilingDownlight({
  position,
  intensity = 0.7,
  distance = 8,
  color = "#fff1d8",
}: {
  position: [number, number, number];
  intensity?: number;
  distance?: number;
  color?: string;
}) {
  return (
    <group position={position}>
      {/* trim ring */}
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.16, 24]} />
        <meshStandardMaterial color="#cfd4d9" roughness={0.6} metalness={0.2} />
      </mesh>
      {/* glowing lens */}
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, -0.005, 0]}>
        <circleGeometry args={[0.12, 24]} />
        <meshStandardMaterial
          color="#fffaf0"
          emissive={new THREE.Color(color)}
          emissiveIntensity={2.0}
          toneMapped={false}
        />
      </mesh>
      <pointLight position={[0, -0.15, 0]} intensity={intensity} distance={distance} decay={2} color={color} />
    </group>
  );
}

/** Skirting board / baseboard trim along the foot of a wall. `horizontal` runs
 *  along x; otherwise along z. */
export function Baseboard({
  position,
  length,
  horizontal = true,
  color = "#c7ccd2",
  height = 0.12,
}: {
  position: [number, number, number];
  length: number;
  horizontal?: boolean;
  color?: string;
  height?: number;
}) {
  const size: [number, number, number] = horizontal
    ? [length, height, 0.05]
    : [0.05, height, length];
  return (
    <mesh position={position} castShadow receiveShadow>
      <boxGeometry args={size} />
      <meshStandardMaterial color={color} roughness={0.7} />
    </mesh>
  );
}

/** A framed window letting in bright daylight: a sky-graded glowing pane behind a
 *  white frame + sill + cross mullions, with a soft point light spilling inward
 *  so the wall reads as a real window, not a poster. Faces +z by default. */
export function DayWindow({
  position,
  rotationDeg = [0, 0, 0],
  w = 2.2,
  h = 1.6,
  spill = 0.8,
}: {
  position: [number, number, number];
  rotationDeg?: [number, number, number];
  w?: number;
  h?: number;
  spill?: number;
}) {
  return (
    <group
      position={position}
      rotation={[
        (rotationDeg[0] * Math.PI) / 180,
        (rotationDeg[1] * Math.PI) / 180,
        (rotationDeg[2] * Math.PI) / 180,
      ]}
    >
      {/* bright sky pane (glowing; the top is bluer, the bottom paler via two bands) */}
      <mesh position={[0, h * 0.25, 0.01]}>
        <planeGeometry args={[w, h * 0.5]} />
        <meshStandardMaterial
          color="#bfe3f5"
          emissive={new THREE.Color("#9fd0ee")}
          emissiveIntensity={0.9}
          toneMapped={false}
        />
      </mesh>
      <mesh position={[0, -h * 0.25, 0.01]}>
        <planeGeometry args={[w, h * 0.5]} />
        <meshStandardMaterial
          color="#dceefb"
          emissive={new THREE.Color("#cfe9f7")}
          emissiveIntensity={0.8}
          toneMapped={false}
        />
      </mesh>
      {/* frame */}
      {[
        { p: [0, h / 2 + 0.06, 0.03], s: [w + 0.24, 0.14, 0.1] },
        { p: [0, -h / 2 - 0.06, 0.03], s: [w + 0.24, 0.14, 0.1] },
        { p: [-w / 2 - 0.07, 0, 0.03], s: [0.12, h + 0.02, 0.1] },
        { p: [w / 2 + 0.07, 0, 0.03], s: [0.12, h + 0.02, 0.1] },
      ].map((b, i) => (
        <mesh key={i} position={b.p as [number, number, number]} castShadow>
          <boxGeometry args={b.s as [number, number, number]} />
          <meshStandardMaterial color="#eef1f4" roughness={0.7} />
        </mesh>
      ))}
      {/* sill */}
      <mesh position={[0, -h / 2 - 0.11, 0.08]} castShadow>
        <boxGeometry args={[w + 0.34, 0.08, 0.2]} />
        <meshStandardMaterial color="#dfe3e8" roughness={0.7} />
      </mesh>
      {/* cross mullions */}
      <mesh position={[0, 0, 0.02]}>
        <boxGeometry args={[w, 0.05, 0.04]} />
        <meshStandardMaterial color="#dfe3e8" roughness={0.6} />
      </mesh>
      <mesh position={[0, 0, 0.02]}>
        <boxGeometry args={[0.05, h, 0.04]} />
        <meshStandardMaterial color="#dfe3e8" roughness={0.6} />
      </mesh>
      {/* daylight spilling into the room */}
      <pointLight position={[0, 0, 0.6]} intensity={spill} distance={7} decay={2} color="#eaf4ff" />
    </group>
  );
}

/** A realistic swivel office chair: contoured padded seat, high reclined backrest
 *  with a lumbar pad + headrest, padded armrests on posts, a tilt mechanism,
 *  gas-lift cylinder and a 5-star base with barrel casters. Shared by every
 *  office-style scene. Default faces -z (toward a desk); pass `position`/
 *  `rotationDeg`/`scale` to reuse it elsewhere. */
export function OfficeChair({
  position = [0, 0, 0.7],
  rotationDeg = [0, 0, 0],
  scale = 1,
}: {
  position?: [number, number, number];
  rotationDeg?: [number, number, number];
  scale?: number;
}) {
  const frame = "#13181d";
  const fabric = "#272e36";
  const fab2 = "#30383f";
  return (
    <group
      position={position}
      rotation={[
        (rotationDeg[0] * Math.PI) / 180,
        (rotationDeg[1] * Math.PI) / 180,
        (rotationDeg[2] * Math.PI) / 180,
      ]}
      scale={scale}
    >
      {/* seat cushion (padded) */}
      <mesh position={[0, 0.47, 0]} castShadow>
        <boxGeometry args={[0.5, 0.08, 0.48]} />
        <meshStandardMaterial color={fabric} roughness={0.85} />
      </mesh>
      {/* front seat lip (slight contour) */}
      <mesh position={[0, 0.45, -0.24]} rotation={[0.25, 0, 0]} castShadow>
        <boxGeometry args={[0.48, 0.06, 0.12]} />
        <meshStandardMaterial color={fab2} roughness={0.85} />
      </mesh>
      {/* tilt mechanism under the seat */}
      <mesh position={[0, 0.41, 0.02]} castShadow>
        <boxGeometry args={[0.18, 0.05, 0.22]} />
        <meshStandardMaterial color={frame} metalness={0.4} roughness={0.5} />
      </mesh>

      {/* backrest (reclined), lumbar pad + headrest */}
      <group position={[0, 0, 0.235]} rotation={[-0.16, 0, 0]}>
        <mesh position={[0, 0.83, 0]} castShadow>
          <boxGeometry args={[0.48, 0.62, 0.07]} />
          <meshStandardMaterial color={fabric} roughness={0.85} />
        </mesh>
        {/* side bolsters */}
        {[-0.205, 0.205].map((x, i) => (
          <mesh key={i} position={[x, 0.82, -0.02]} castShadow>
            <boxGeometry args={[0.06, 0.6, 0.1]} />
            <meshStandardMaterial color={fab2} roughness={0.85} />
          </mesh>
        ))}
        {/* lumbar pad */}
        <mesh position={[0, 0.62, -0.05]} castShadow>
          <boxGeometry args={[0.34, 0.16, 0.06]} />
          <meshStandardMaterial color={fab2} roughness={0.9} />
        </mesh>
        {/* headrest */}
        <mesh position={[0, 1.2, -0.01]} castShadow>
          <boxGeometry args={[0.3, 0.16, 0.07]} />
          <meshStandardMaterial color={fabric} roughness={0.85} />
        </mesh>
        {/* headrest posts */}
        {[-0.08, 0.08].map((x, i) => (
          <mesh key={i} position={[x, 1.08, -0.03]} castShadow>
            <boxGeometry args={[0.025, 0.12, 0.025]} />
            <meshStandardMaterial color={frame} metalness={0.5} roughness={0.4} />
          </mesh>
        ))}
        {/* backrest spine into the seat */}
        <mesh position={[0, 0.5, -0.06]} castShadow>
          <boxGeometry args={[0.1, 0.5, 0.05]} />
          <meshStandardMaterial color={frame} metalness={0.5} roughness={0.4} />
        </mesh>
      </group>

      {/* armrests: padded top on an L post */}
      {[-0.3, 0.3].map((x, i) => (
        <group key={i}>
          <mesh position={[x, 0.55, 0.04]} castShadow>
            <boxGeometry args={[0.04, 0.16, 0.04]} />
            <meshStandardMaterial color={frame} metalness={0.5} roughness={0.4} />
          </mesh>
          <mesh position={[x, 0.64, 0.0]} castShadow>
            <boxGeometry args={[0.07, 0.04, 0.3]} />
            <meshStandardMaterial color={fab2} roughness={0.8} />
          </mesh>
        </group>
      ))}

      {/* gas-lift cylinder */}
      <mesh position={[0, 0.26, 0]} castShadow>
        <cylinderGeometry args={[0.035, 0.04, 0.32, 16]} />
        <meshStandardMaterial color={frame} metalness={0.6} roughness={0.35} />
      </mesh>
      {/* base hub */}
      <mesh position={[0, 0.1, 0]} castShadow>
        <cylinderGeometry args={[0.06, 0.07, 0.05, 16]} />
        <meshStandardMaterial color={frame} metalness={0.6} roughness={0.4} />
      </mesh>

      {/* 5-star base spokes + barrel casters */}
      {Array.from({ length: 5 }).map((_, i) => {
        const a = (i / 5) * Math.PI * 2;
        const lx = Math.sin(a) * 0.14;
        const lz = Math.cos(a) * 0.14;
        const ex = Math.sin(a) * 0.28;
        const ez = Math.cos(a) * 0.28;
        return (
          <group key={i}>
            <mesh position={[lx, 0.085, lz]} rotation={[0, -a, 0.06]} castShadow>
              <boxGeometry args={[0.055, 0.045, 0.3]} />
              <meshStandardMaterial color={frame} metalness={0.55} roughness={0.4} />
            </mesh>
            <mesh position={[ex, 0.05, ez]} castShadow>
              <cylinderGeometry args={[0.026, 0.026, 0.04, 12]} />
              <meshStandardMaterial color="#0c0f13" roughness={0.5} />
            </mesh>
            <mesh position={[ex, 0.028, ez]} rotation={[Math.PI / 2, 0, -a]} castShadow>
              <cylinderGeometry args={[0.03, 0.03, 0.04, 14]} />
              <meshStandardMaterial color="#070a0d" roughness={0.45} metalness={0.2} />
            </mesh>
          </group>
        );
      })}
    </group>
  );
}
